import React, { useState, useMemo } from "react";
import { View, TouchableOpacity } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { AppText } from "@/components/AppText";
import { HotelReservationWithRefundProcess } from "@/types/hotelReservation";
import { PhoneRefundFlowModal } from "./PhoneRefundFlowModal";
import { FontFamilies } from "@/constants/Typography";
import Icons from "@/assets/icons";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import { useTrackImpression, useTrackClick } from "@/hooks/tracking";

type Props = {
  reservation: HotelReservationWithRefundProcess;
};

export const HotelReservationCard: React.FC<Props> = ({ reservation }) => {
  const [showRefundModal, setShowRefundModal] = useState(false);

  const platformName = useMemo(
    () => reservation.hotelPlatform || "Unknown",
    [reservation.hotelPlatform]
  );

  // Track when card is shown
  useTrackImpression(TRACKING_EVENTS.HOTEL_REFUND_SHOWN, {
    hotelName: reservation.hotelName,
    platform: platformName,
    amount: reservation.usdPrice,
  });

  // Use stored potential savings from backend, or calculate from live prices as fallback
  const displayAmount = React.useMemo(() => {
    // Prefer stored potential savings (set when email was sent)
    if (reservation.potentialSavings && reservation.potentialSavings > 0) {
      return reservation.potentialSavings;
    }

    // Fallback: calculate from live prices if available
    if (reservation.hotelPrices && reservation.hotelPrices.length > 0) {
      const lowestPrice = Math.min(
        ...reservation.hotelPrices.map(
          (price) => (price.price || 0) + (price.tax || 0)
        )
      );
      return (reservation.usdPrice ?? 0) - lowestPrice;
    }

    return 0;
  }, [reservation.potentialSavings, reservation.hotelPrices, reservation.usdPrice]);

  // Calculate the status text to display
  const statusText = React.useMemo(() => {
    // If we have stored savings, show a simple message
    if (reservation.potentialSavings && reservation.potentialSavings > 0) {
      return "Price drop found for your booking";
    }

    // Fallback: show vendor info from live prices
    if (reservation.hotelPrices && reservation.hotelPrices.length > 0) {
      let lowestPrice = Infinity;
      let bestVendor = "";

      reservation.hotelPrices.forEach((price) => {
        const totalPrice = (price.price || 0) + (price.tax || 0);
        if (totalPrice < lowestPrice) {
          lowestPrice = totalPrice;
          bestVendor = price.vendor;
        }
      });

      const savings = (reservation.usdPrice ?? 0) - lowestPrice;
      if (savings > 0) {
        return `Found $${savings.toFixed(2)} better price on ${bestVendor}`;
      }
    }
    return "No other prices found.";
  }, [reservation.potentialSavings, reservation.hotelPrices, reservation.usdPrice]);

  // Track when "Request refund" button is clicked
  const handleStartRefund = useTrackClick(
    TRACKING_EVENTS.HOTEL_REFUND_CLICKED,
    {
      hotelName: reservation.hotelName,
      platform: platformName,
      amount: reservation.usdPrice,
    }
  )(() => {
    setShowRefundModal(true);
  });

  const handleCloseModals = () => {
    setShowRefundModal(false);
  };

  // Don't show the card if there are no actual savings
  if (displayAmount <= 0) {
    return null;
  }

  return (
    <View style={styles.card}>
      {/* Top section with image and title/amount */}
      <View style={styles.topSection}>
        <View style={styles.productImageContainer}>
          <View style={styles.hotelLogo}>
            <AppText style={styles.productLogoText}>
              {reservation.hotelName.charAt(0).toUpperCase()}
            </AppText>
          </View>
        </View>
        <View style={styles.leftContent}>
          <AppText style={styles.titleText}>
            Price match on {platformName} for {reservation.hotelName}
          </AppText>
          <AppText style={styles.amountText}>
            ${displayAmount.toFixed(2)}
          </AppText>
        </View>
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Bottom row: status and request button */}
      <View style={styles.buttonSection}>
        <View style={styles.statusContent}>
          <Icons.NoProofPaper width={16} height={16} />
          <AppText style={styles.statusText}>{statusText}</AppText>
        </View>
        <TouchableOpacity
          style={styles.requestButton}
          onPress={handleStartRefund}
        >
          <AppText style={styles.requestButtonText}>Request refund</AppText>
        </TouchableOpacity>
      </View>

      <PhoneRefundFlowModal
        visible={showRefundModal}
        item={reservation}
        onClose={handleCloseModals}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: "#EBEBEB",
    overflow: "hidden",
  },
  topSection: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
  },
  leftContent: {
    flex: 1,
    marginLeft: 16,
  },
  amountText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 20,
    color: "#161D14",
  },
  titleText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    color: "#161D14",
  },
  productImageContainer: {
    width: 80,
    height: 80,
    borderRadius: 16,
    backgroundColor: "#F7F8FA",
    justifyContent: "center",
    alignItems: "center",
  },
  hotelLogo: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: "#E5E7EB",
    justifyContent: "center",
    alignItems: "center",
  },
  productLogoText: {
    fontSize: 24,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
  statusText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    fontWeight: "400",
    color: "#161D14",
    lineHeight: 13 * 1.5385, // 20px from Figma
    letterSpacing: -0.02 * 13, // -2% of font size
  },
  buttonSection: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    padding: 8, // Reduced from 16 to 8px to match Figma
    paddingHorizontal: 16,
  },
  statusContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flex: 1,
  },
  requestButton: {
    backgroundColor: "#0F9129",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.12)",
    borderRadius: 100,
    paddingHorizontal: 20,
    height: 39,
    justifyContent: "center",
    alignItems: "center",
  },
  requestButtonText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 15,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});
